/**
 * Missing Fish Detection - Biomass Reconciliation Analysis
 * 
 * Uses the daily_analysis_rows foundation dataset to estimate potential
 * missing fish through feed-implied biomass reconciliation.
 * 
 * Calculations run ONLY at sample dates for credit efficiency.
 */

import { calculateBiologicalFCR, resolveFishCount, detectSampleAnomalies, applyAnomalyPenalty } from './calculationEngine';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// WEIGHT-DEPENDENT MODEL FCR LOOKUP (replaces hardcoded constants)
// ============================================================================

// Cache for fetched model FCR values to avoid repeated API calls
const _fcrCache = {};

/**
 * Get weight-dependent model FCR from the backend lookup tables.
 * Falls back to a conservative estimate if the API is unavailable.
 *
 * The cache key AND the request URL both include `fcrVersionId` so
 * that switching FCR Version dropdowns (e.g. Italian v1 100% → Italian
 * @105%) invalidates the previous cached value and forces a fresh
 * multiplied lookup. Prior to Feb 2026 this parameter was omitted from
 * both places, which silently pinned the biological FCR at whichever
 * version was warmed first — making Missing Fish Intelligence report
 * the same estimate across every version pick.
 *
 * @param {string} species      - Species name
 * @param {number} weightG      - Fish weight in grams
 * @param {string} geneticOrigin - Genetic origin (for Seabream)
 * @param {string|null} fcrVersionId - FCR version selection (uuid) — the
 *   backend applies the version's multiplier on top of the base curve.
 * @returns {Promise<number>} Model FCR value
 */
export const getModelFCR = async (species, weightG, geneticOrigin = 'Greek', fcrVersionId = null) => {
  const versionKey = fcrVersionId || 'default';
  const cacheKey = `${species}_${Math.round(weightG)}_${geneticOrigin}_${versionKey}`;
  if (_fcrCache[cacheKey]) return _fcrCache[cacheKey];

  try {
    const versionQ = fcrVersionId ? `&fcr_version_id=${encodeURIComponent(fcrVersionId)}` : '';
    const res = await fetch(`${API}/api/model-fcr?species=${encodeURIComponent(species)}&weight_g=${weightG}&genetic_origin=${encodeURIComponent(geneticOrigin)}${versionQ}`);
    const data = await res.json();
    if (data.model_fcr) {
      _fcrCache[cacheKey] = data.model_fcr;
      return data.model_fcr;
    }
  } catch {
    // API unavailable — fall through to conservative estimate
  }

  // Conservative fallback (only used when API is unreachable)
  const fallback = species?.toLowerCase() === 'tilapia' ? 1.8 : 1.6;
  _fcrCache[cacheKey] = fallback;
  return fallback;
};

/**
 * Synchronous FCR getter using cached values.
 * Must call getModelFCR() at least once before using this for the same parameters.
 *
 * @param {string} species
 * @param {number} weightG
 * @param {string} geneticOrigin
 * @param {string|null} fcrVersionId
 * @returns {number} Cached model FCR or conservative fallback
 */
export const getModelFCRSync = (species, weightG, geneticOrigin = 'Greek', fcrVersionId = null) => {
  const versionKey = fcrVersionId || 'default';
  const cacheKey = `${species}_${Math.round(weightG)}_${geneticOrigin}_${versionKey}`;
  return _fcrCache[cacheKey] || (species?.toLowerCase() === 'tilapia' ? 1.8 : 1.6);
};

/**
 * Get expected FCR for calculations — uses weight-dependent cache.
 * @param {string} species - Species name
 * @param {number|null} userProvidedFCR - User-provided FCR if available
 * @param {number} weightG - Current fish weight for weight-dependent lookup
 * @param {string} geneticOrigin - Genetic origin
 * @param {string|null} fcrVersionId - FCR version selection (uuid)
 * @returns {number} FCR value to use
 */
export const getExpectedFCR = (species, userProvidedFCR = null, weightG = 100, geneticOrigin = 'Greek', fcrVersionId = null) => {
  if (userProvidedFCR !== null && userProvidedFCR > 0) {
    return userProvidedFCR;
  }
  return getModelFCRSync(species, weightG, geneticOrigin, fcrVersionId);
};

// ============================================================================
// BIOMASS RECONCILIATION AT SAMPLE DATES
// ============================================================================

/**
 * Calculate feed-implied biomass at each sample date
 * 
 * Formula: feed_implied_biomass_kg = starting_biomass_kg + (cumulative_feed_kg / expected_FCR)
 * 
 * @param {Array} daily_analysis_rows - Foundation dataset rows
 * @param {number} startingBiomassKg - Initial stocking biomass (input_average_weight_g × input_number / 1000)
 * @param {number} expectedFCR - Expected FCR value
 * @returns {Array} Sample rows with feed-implied biomass added
 */
export const calculateBiomassReconciliation = (daily_analysis_rows, startingBiomassKg, expectedFCR) => {
  if (!daily_analysis_rows || daily_analysis_rows.length === 0) {
    return [];
  }
  
  // Filter to only sample dates
  const sampleRows = daily_analysis_rows.filter(row => row.is_sample && row.actual_weight_g !== null);
  
  if (sampleRows.length === 0) {
    return [];
  }
  
  // Add feed-implied biomass to each sample row
  return sampleRows.map(row => {
    // feed_implied_biomass_kg = starting_biomass_kg + (cumulative_feed_kg / expected_FCR)
    const feed_implied_biomass_kg = row.cumulative_feed_kg !== null && row.cumulative_feed_kg > 0
      ? startingBiomassKg + (row.cumulative_feed_kg / expectedFCR)
      : null;
    
    return {
      ...row,
      feed_implied_biomass_kg: feed_implied_biomass_kg !== null 
        ? Math.round(feed_implied_biomass_kg * 100) / 100 
        : null,
      starting_biomass_kg: Math.round(startingBiomassKg * 100) / 100,
    };
  });
};

// ============================================================================
// MISSING FISH DETECTION
// ============================================================================

/**
 * Detect potential missing fish based on biomass reconciliation
 * 
 * Conditions (all must be true):
 * 1. actual_weight_g > model_weight_g by more than 5%
 * 2. feed_implied_biomass_kg < model_biomass_kg
 * 3. Pattern persists across 2 consecutive samples
 * 
 * @param {Array} reconciliationRows - Rows from calculateBiomassReconciliation
 * @param {number} declaredFishCount - Current declared/assumed fish count
 * @returns {Object} Missing fish detection results
 */
export const detectMissingFish = (reconciliationRows, declaredFishCount) => {
  if (!reconciliationRows || reconciliationRows.length < 2) {
    return {
      detected: false,
      reason: 'Insufficient sample data (need at least 2 samples)',
      estimatedFishCount: null,
      missingFish: null,
      missingPercent: null,
      confirmingSamples: [],
    };
  }
  
  // Check each pair of consecutive samples for the 3 conditions
  const confirmingSamples = [];
  
  for (let i = 0; i < reconciliationRows.length - 1; i++) {
    const current = reconciliationRows[i];
    const next = reconciliationRows[i + 1];
    
    // Check all 3 conditions for current sample
    const currentDeviationPercent = current.model_weight_g > 0
      ? ((current.actual_weight_g - current.model_weight_g) / current.model_weight_g) * 100
      : 0;
    
    const nextDeviationPercent = next.model_weight_g > 0
      ? ((next.actual_weight_g - next.model_weight_g) / next.model_weight_g) * 100
      : 0;
    
    // Condition 1: actual_weight_g > model_weight_g by more than 5%
    const condition1_current = currentDeviationPercent > 5;
    const condition1_next = nextDeviationPercent > 5;
    
    // Condition 2: feed_implied_biomass_kg < model_biomass_kg
    const condition2_current = current.feed_implied_biomass_kg !== null && 
                                current.model_biomass_kg !== null &&
                                current.feed_implied_biomass_kg < current.model_biomass_kg;
    const condition2_next = next.feed_implied_biomass_kg !== null && 
                             next.model_biomass_kg !== null &&
                             next.feed_implied_biomass_kg < next.model_biomass_kg;
    
    // Condition 3: Pattern persists across 2 consecutive samples
    const allConditionsMet = condition1_current && condition1_next && 
                              condition2_current && condition2_next;
    
    if (allConditionsMet) {
      confirmingSamples.push({
        sampleIndex: i,
        date: current.date,
        deviationPercent: Math.round(currentDeviationPercent * 10) / 10,
        feedImpliedBiomass: current.feed_implied_biomass_kg,
        modelBiomass: current.model_biomass_kg,
      });
      
      // Also add the confirming (next) sample
      if (i === reconciliationRows.length - 2) {
        confirmingSamples.push({
          sampleIndex: i + 1,
          date: next.date,
          deviationPercent: Math.round(nextDeviationPercent * 10) / 10,
          feedImpliedBiomass: next.feed_implied_biomass_kg,
          modelBiomass: next.model_biomass_kg,
        });
      }
    }
  }
  
  // If no confirming samples found, return no detection
  if (confirmingSamples.length === 0) {
    return {
      detected: false,
      reason: 'Conditions not met: Pattern does not persist across consecutive samples',
      estimatedFishCount: null,
      missingFish: null,
      missingPercent: null,
      confirmingSamples: [],
    };
  }
  
  // Calculate estimated fish count using the latest confirming sample
  const latestSample = reconciliationRows[reconciliationRows.length - 1];
  
  // estimated_fish_count = feed_implied_biomass_kg / actual_sample_weight_kg
  // where actual_sample_weight_kg = actual_weight_g / 1000
  const actualSampleWeightKg = latestSample.actual_weight_g / 1000;
  
  let estimatedFishCount = null;
  let missingFish = null;
  let missingPercent = null;
  
  if (latestSample.feed_implied_biomass_kg !== null && actualSampleWeightKg > 0) {
    estimatedFishCount = Math.round(latestSample.feed_implied_biomass_kg / actualSampleWeightKg);
    
    // missing_fish = declared_fish_count − estimated_fish_count
    missingFish = declaredFishCount - estimatedFishCount;
    
    // missing_percent = missing_fish / declared_fish_count × 100
    missingPercent = declaredFishCount > 0
      ? Math.round((missingFish / declaredFishCount) * 100 * 10) / 10
      : null;
  }
  
  return {
    detected: true,
    reason: 'All conditions met: Deviation >5%, feed-implied biomass divergence, pattern confirmed',
    estimatedFishCount,
    missingFish,
    missingPercent,
    confirmingSamples,
    calculationDetails: {
      declaredFishCount,
      feedImpliedBiomassKg: latestSample.feed_implied_biomass_kg,
      actualSampleWeightKg: Math.round(actualSampleWeightKg * 1000) / 1000,
      sampleDate: latestSample.date,
    },
  };
};

// ============================================================================
// EARLIEST ACTIONABLE DETECTION
// ============================================================================

/**
 * Find the earliest actionable detection date
 * 
 * Criteria:
 * 1. deviation > +5%
 * 2. next sample confirms deviation
 * 3. feed-implied biomass diverges in same direction
 * 
 * @param {Array} reconciliationRows - Rows from calculateBiomassReconciliation
 * @returns {Object} Earliest actionable detection result
 */
export const findEarliestActionableDate = (reconciliationRows) => {
  if (!reconciliationRows || reconciliationRows.length < 2) {
    return {
      found: false,
      date: null,
      dayIndex: null,
      reason: 'Insufficient samples for actionable detection',
    };
  }
  
  for (let i = 0; i < reconciliationRows.length - 1; i++) {
    const current = reconciliationRows[i];
    const next = reconciliationRows[i + 1];
    
    // Calculate deviations
    const currentDeviationPercent = current.model_weight_g > 0
      ? ((current.actual_weight_g - current.model_weight_g) / current.model_weight_g) * 100
      : 0;
    
    const nextDeviationPercent = next.model_weight_g > 0
      ? ((next.actual_weight_g - next.model_weight_g) / next.model_weight_g) * 100
      : 0;
    
    // Check criteria
    const deviationAboveThreshold = currentDeviationPercent > 5;
    const nextConfirms = nextDeviationPercent > 5;
    const feedImpliedDiverges = current.feed_implied_biomass_kg !== null &&
                                  current.model_biomass_kg !== null &&
                                  current.feed_implied_biomass_kg < current.model_biomass_kg;
    
    if (deviationAboveThreshold && nextConfirms && feedImpliedDiverges) {
      return {
        found: true,
        date: current.date,
        dayIndex: current.day_index,
        deviationPercent: Math.round(currentDeviationPercent * 10) / 10,
        reason: 'Deviation confirmed by subsequent sample with feed-implied divergence',
      };
    }
  }
  
  return {
    found: false,
    date: null,
    dayIndex: null,
    reason: 'No actionable detection point found',
  };
};

// ============================================================================
// AVOIDABLE FEED ESTIMATE
// ============================================================================

/**
 * Calculate avoidable feed estimate
 * 
 * Formula:
 * missing_fraction = missing_fish / declared_fish_count
 * avoidable_feed_kg = sum(daily_feed_kg after earliest actionable date) × missing_fraction
 * 
 * @param {Array} daily_analysis_rows - Foundation dataset rows
 * @param {Object} earliestActionable - Result from findEarliestActionableDate
 * @param {number} missingFish - Estimated missing fish count
 * @param {number} declaredFishCount - Declared fish count
 * @returns {Object} Avoidable feed estimate
 */
export const calculateAvoidableFeed = (daily_analysis_rows, earliestActionable, missingFish, declaredFishCount) => {
  if (!earliestActionable?.found || !missingFish || missingFish <= 0 || declaredFishCount <= 0) {
    return {
      available: false,
      avoidableFeedKg: null,
      missingFraction: null,
      periodFeedKg: null,
      reason: 'Cannot calculate: Missing fish not detected or no actionable date',
    };
  }
  
  // Calculate missing fraction
  const missingFraction = missingFish / declaredFishCount;
  
  // Sum daily feed after earliest actionable date
  const earliestDate = new Date(earliestActionable.date);
  let periodFeedKg = 0;
  
  for (const row of daily_analysis_rows) {
    const rowDate = new Date(row.date);
    if (rowDate >= earliestDate && row.daily_feed_kg !== null) {
      periodFeedKg += row.daily_feed_kg;
    }
  }
  
  // avoidable_feed_kg = period_feed × missing_fraction
  const avoidableFeedKg = periodFeedKg * missingFraction;
  
  return {
    available: true,
    avoidableFeedKg: Math.round(avoidableFeedKg * 100) / 100,
    missingFraction: Math.round(missingFraction * 1000) / 1000,
    missingFractionPercent: Math.round(missingFraction * 100 * 10) / 10,
    periodFeedKg: Math.round(periodFeedKg * 100) / 100,
    startDate: earliestActionable.date,
  };
};

// ============================================================================
// CONFIDENCE CLASSIFICATION
// ============================================================================

/**
 * Classify confidence level based on analysis results
 * 
 * High: 2+ consecutive deviations >5% AND feed-implied biomass supports deviation
 * Moderate: deviation exists but feed signal weak
 * Low: single deviation or high sample volatility
 * 
 * @param {Array} reconciliationRows - Rows from calculateBiomassReconciliation
 * @param {Object} missingFishResult - Result from detectMissingFish
 * @returns {Object} Confidence classification
 */
export const classifyConfidence = (reconciliationRows, missingFishResult) => {
  if (!reconciliationRows || reconciliationRows.length === 0) {
    return {
      level: 'Low',
      score: 0,
      class: 'bg-green-100 text-green-700 border-green-300',
      factors: ['Insufficient data for analysis'],
    };
  }
  
  // Calculate deviations
  const deviations = reconciliationRows.map(row => ({
    percent: row.model_weight_g > 0
      ? ((row.actual_weight_g - row.model_weight_g) / row.model_weight_g) * 100
      : 0,
    feedDiverges: row.feed_implied_biomass_kg !== null && 
                   row.model_biomass_kg !== null &&
                   row.feed_implied_biomass_kg < row.model_biomass_kg,
  }));
  
  // Count consecutive deviations >5%
  let consecutiveCount = 0;
  let maxConsecutive = 0;
  for (const d of deviations) {
    if (d.percent > 5) {
      consecutiveCount++;
      maxConsecutive = Math.max(maxConsecutive, consecutiveCount);
    } else {
      consecutiveCount = 0;
    }
  }
  
  // Count feed-aligned divergences
  const feedAlignedCount = deviations.filter(d => d.percent > 5 && d.feedDiverges).length;
  
  // Calculate volatility (standard deviation of deviations)
  const devValues = deviations.map(d => d.percent);
  const mean = devValues.reduce((a, b) => a + b, 0) / devValues.length;
  const variance = devValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / devValues.length;
  const stdDev = Math.sqrt(variance);
  
  const factors = [];
  let score = 0;
  
  // High confidence criteria
  if (maxConsecutive >= 2 && feedAlignedCount >= 2) {
    score = 80;
    factors.push(`${maxConsecutive} consecutive samples with >5% deviation`);
    factors.push(`${feedAlignedCount} samples with feed-implied divergence`);
    
    return {
      level: 'High',
      score,
      class: 'bg-red-100 text-red-700 border-red-300',
      factors,
    };
  }
  
  // Moderate confidence criteria
  const hasDeviation = deviations.some(d => d.percent > 5);
  const weakFeedSignal = feedAlignedCount < 2;
  
  if (hasDeviation && weakFeedSignal) {
    score = 50;
    factors.push('Deviation detected but feed signal weak');
    if (feedAlignedCount === 1) factors.push('Only 1 sample with feed alignment');
    
    return {
      level: 'Moderate',
      score,
      class: 'bg-amber-100 text-amber-700 border-amber-300',
      factors,
    };
  }
  
  // Low confidence criteria
  factors.push(maxConsecutive <= 1 ? 'Single deviation only' : 'No significant deviation');
  if (stdDev > 15) factors.push(`High sample volatility (σ=${stdDev.toFixed(1)}%)`);
  
  return {
    level: 'Low',
    score: 20,
    class: 'bg-green-100 text-green-700 border-green-300',
    factors,
  };
};

// ============================================================================
// MAIN ANALYSIS FUNCTION
// ============================================================================

/**
 * Run complete Missing Fish Detection analysis
 * 
 * @param {Array} daily_analysis_rows - Foundation dataset
 * @param {Object} activeRecord - Active spreadsheet record containing:
 *   - input_average_weight: Initial average weight (g)
 *   - input_number: Initial fish count
 *   - species: Species name
 *   - sample_points: Array of sample data
 * @param {number|null} userProvidedFCR - User-provided expected FCR (optional)
 * @returns {Object} Complete analysis results
 */
export const runMissingFishDetection = (daily_analysis_rows, activeRecord, userProvidedFCR = null) => {
  // Validate required data
  if (!daily_analysis_rows || daily_analysis_rows.length === 0) {
    return {
      available: false,
      message: 'Missing fish estimate unavailable due to insufficient data.',
      reason: 'No daily analysis data available',
    };
  }
  
  if (!activeRecord) {
    return {
      available: false,
      message: 'Missing fish estimate unavailable due to insufficient data.',
      reason: 'No active record',
    };
  }
  
  // Check for required stocking inputs
  const inputWeight = activeRecord.input_average_weight;
  const inputNumber = activeRecord.input_number;
  
  if (!inputWeight || !inputNumber) {
    return {
      available: false,
      message: 'Missing fish estimate unavailable due to insufficient data.',
      reason: 'Missing stocking inputs (initial weight or fish count)',
    };
  }
  
  // Check for feed data
  const hasFeedData = daily_analysis_rows.some(row => row.daily_feed_kg !== null && row.daily_feed_kg > 0);
  if (!hasFeedData) {
    return {
      available: false,
      message: 'Missing fish estimate unavailable due to insufficient data.',
      reason: 'No feed data provided in spreadsheet',
    };
  }
  
  // Check for sample weights
  const hasSampleWeights = daily_analysis_rows.some(row => row.is_sample && row.actual_weight_g !== null);
  if (!hasSampleWeights) {
    return {
      available: false,
      message: 'Missing fish estimate unavailable due to insufficient data.',
      reason: 'No sample weight data available',
    };
  }
  
  // Calculate starting biomass
  const startingBiomassKg = (inputWeight * inputNumber) / 1000;
  
  // Get weight-dependent expected FCR
  const species = activeRecord.species || 'Seabream';
  const lastSample = daily_analysis_rows.filter(r => r.is_sample && r.actual_weight_g > 0).pop();
  const currentWeightG = lastSample?.actual_weight_g || inputWeight;
  const geneticOrigin = activeRecord.genetic_origin || 'Greek';
  const fcrVersionId = activeRecord.fcr_version_id || null;
  const expectedFCR = getExpectedFCR(species, userProvidedFCR, currentWeightG, geneticOrigin, fcrVersionId);
  
  // Run anomaly detection on sample data
  const samplePoints = daily_analysis_rows
    .filter(r => r.is_sample && r.actual_weight_g > 0)
    .map((r, idx) => ({ weight_g: r.actual_weight_g, day: r.day_number || idx, date: r.date }));
  const sampleAnomalies = detectSampleAnomalies(samplePoints, species);
  
  // Calculate biomass reconciliation at sample dates
  const reconciliationRows = calculateBiomassReconciliation(
    daily_analysis_rows, 
    startingBiomassKg, 
    expectedFCR
  );
  
  if (reconciliationRows.length === 0) {
    return {
      available: false,
      message: 'Missing fish estimate unavailable due to insufficient data.',
      reason: 'No valid sample data for reconciliation',
    };
  }
  
  // Get declared fish count (current, after mortality/harvests)
  const lastRow = daily_analysis_rows[daily_analysis_rows.length - 1];
  const declaredFishCount = lastRow?.fish_count || inputNumber;
  
  // Detect missing fish
  const missingFishResult = detectMissingFish(reconciliationRows, declaredFishCount);
  
  // Find earliest actionable date
  const earliestActionable = findEarliestActionableDate(reconciliationRows);
  
  // Calculate avoidable feed
  const avoidableFeed = calculateAvoidableFeed(
    daily_analysis_rows,
    earliestActionable,
    missingFishResult.missingFish,
    declaredFishCount
  );
  
  // Classify confidence
  const confidence = classifyConfidence(reconciliationRows, missingFishResult);
  
  // Biomass uncertainty range (±5% simple tolerance on latest actual biomass)
  const latestBiomassRow = reconciliationRows[reconciliationRows.length - 1];
  const biomassEstimateKg = latestBiomassRow?.actual_biomass_kg ?? null;
  const biomassRange = biomassEstimateKg != null ? {
    estimateKg: Math.round(biomassEstimateKg * 100) / 100,
    lowKg:  Math.round(biomassEstimateKg * 0.95 * 100) / 100,
    highKg: Math.round(biomassEstimateKg * 1.05 * 100) / 100,
    estimateTonnes: Math.round(biomassEstimateKg / 1000 * 100) / 100,
    lowTonnes:  Math.round(biomassEstimateKg * 0.95 / 1000 * 100) / 100,
    highTonnes: Math.round(biomassEstimateKg * 1.05 / 1000 * 100) / 100,
    sampleDate: latestBiomassRow.date || null,
  } : null;

  return {
    available: true,
    message: null,
    
    // Input parameters
    inputs: {
      startingBiomassKg: Math.round(startingBiomassKg * 100) / 100,
      expectedFCR,
      species,
      declaredFishCount,
      inputWeight,
      inputNumber,
    },
    
    // Core results
    missingFish: missingFishResult,
    earliestActionable,
    avoidableFeed,
    confidence,
    biomassRange,
    
    // Sample anomaly flags
    sampleAnomalies,
    
    // Reconciliation data for charts/tables
    reconciliationRows,
  };
};

// ============================================================================
// ECONOMIC IMPACT CALCULATIONS
// ============================================================================

/**
 * Calculate economic impact using existing stored analysis outputs.
 * Does NOT rerun biomass calculations — only uses pre-computed values.
 *
 * @param {Object} detectionResults - Output from runMissingFishDetection()
 * @param {Object} economicParams - User-provided economic parameters:
 *   - average_feed_price_eur_per_kg (default 1.50)
 *   - fish_price_eur_per_kg (optional)
 *   - target_harvest_weight_kg (optional)
 * @returns {Object} Economic impact results
 */
export const calculateEconomicImpact = (detectionResults, economicParams = {}) => {
  const feedPrice = economicParams.average_feed_price_eur_per_kg ?? 1.50;
  const fishPrice = economicParams.fish_price_eur_per_kg ?? null;
  const targetWeight = economicParams.target_harvest_weight_kg ?? null;

  const result = {
    available: false,
    feedPrice,
    fishPrice,
    targetWeight,
    avoidableFeedCost: null,
    revenueRisk: null,
    delayedDetectionCost: null,
  };

  if (!detectionResults || !detectionResults.available) {
    return result;
  }

  const avoidableFeedKg = detectionResults.avoidableFeed?.avoidableFeedKg;
  const missingFish = detectionResults.missingFish?.missingFish;

  // FEATURE 1 — Avoidable Feed Cost
  if (avoidableFeedKg != null && avoidableFeedKg > 0) {
    result.avoidableFeedCost = {
      value: Math.round(avoidableFeedKg * feedPrice * 100) / 100,
      avoidableFeedKg,
      feedPrice,
    };
    result.available = true;
  }

  // FEATURE 2 — Missing Fish Revenue Risk
  if (missingFish != null && missingFish > 0 && fishPrice != null && targetWeight != null) {
    result.revenueRisk = {
      value: Math.round(missingFish * targetWeight * fishPrice * 100) / 100,
      missingFish,
      targetWeight,
      fishPrice,
    };
    result.available = true;
  }

  // FEATURE 3 — Cost of Delayed Detection
  // Uses stored feed totals (avoidableFeedKg already computed from detection)
  if (avoidableFeedKg != null && avoidableFeedKg > 0) {
    result.delayedDetectionCost = {
      avoidableFeedKg,
      avoidableFeedCostEur: Math.round(avoidableFeedKg * feedPrice * 100) / 100,
      feedPrice,
      startDate: detectionResults.earliestActionable?.date || null,
    };
    result.available = true;
  }

  return result;
};

// ============================================================================
// DATA QUALITY INDICATOR
// ============================================================================

/**
 * Calculate Data Quality Score (0–100) from rule-based sub-scores.
 *
 * Weights:
 *   sample_frequency  = 40 %
 *   feed_continuity   = 25 %
 *   mortality_reporting = 20 %
 *   temperature_data  = 15 %
 *
 * @param {Array}  dataPoints   – raw data_points from the record
 * @param {Array}  samplePoints – sample_points from the record
 * @param {number} daysInWater  – total valid daily rows
 * @returns {Object} { score, subscores, warning }
 */
export const calculateDataQualityScore = (dataPoints, samplePoints, daysInWater) => {
  if (!dataPoints || dataPoints.length === 0 || daysInWater <= 0) {
    return {
      score: 0,
      subscores: { sampleFrequency: 0, feedContinuity: 0, mortalityReporting: 0, temperatureData: 0 },
      warning: 'Low data reliability may affect analysis accuracy.',
    };
  }

  // --- Sample Frequency Score ---
  const sampleCount = (samplePoints || []).length;
  let sampleFrequency = 0;
  if (sampleCount >= 2) {
    const avgInterval = daysInWater / sampleCount;
    if (avgInterval <= 14)      sampleFrequency = 100;
    else if (avgInterval <= 21) sampleFrequency = 90;
    else if (avgInterval <= 30) sampleFrequency = 70;
    else if (avgInterval <= 45) sampleFrequency = 50;
    else                        sampleFrequency = 30;
  } else if (sampleCount === 1) {
    sampleFrequency = 20;
  }

  // --- Feed Continuity Score ---
  let feedDays = 0;
  for (const dp of dataPoints) {
    if (dp.feed !== null && dp.feed !== undefined && typeof dp.feed === 'number' && dp.feed > 0) {
      feedDays++;
    }
  }
  const feedRatio = feedDays / daysInWater;
  let feedContinuity = 0;
  if (feedRatio >= 0.95)      feedContinuity = 100;
  else if (feedRatio >= 0.85) feedContinuity = 80;
  else if (feedRatio >= 0.70) feedContinuity = 60;
  else if (feedRatio >= 0.50) feedContinuity = 40;
  else                        feedContinuity = 20;

  // --- Mortality Reporting Score ---
  let mortalityReportedDays = 0;
  for (const dp of dataPoints) {
    if (dp.mortality !== null && dp.mortality !== undefined) {
      mortalityReportedDays++;
    }
  }
  const mortalityRatio = mortalityReportedDays / daysInWater;
  let mortalityReporting = 0;
  if (mortalityRatio >= 0.95)      mortalityReporting = 100;
  else if (mortalityRatio >= 0.85) mortalityReporting = 80;
  else if (mortalityRatio >= 0.70) mortalityReporting = 60;
  else if (mortalityRatio >= 0.50) mortalityReporting = 40;
  else                             mortalityReporting = 20;

  // --- Temperature Data Score ---
  let tempDays = 0;
  for (const dp of dataPoints) {
    if (dp.temperature !== null && dp.temperature !== undefined && typeof dp.temperature === 'number') {
      tempDays++;
    }
  }
  const tempRatio = tempDays / daysInWater;
  let temperatureData = 0;
  if (tempRatio >= 0.95)      temperatureData = 100;
  else if (tempRatio >= 0.85) temperatureData = 80;
  else if (tempRatio >= 0.70) temperatureData = 60;
  else if (tempRatio >= 0.50) temperatureData = 40;
  else                        temperatureData = 20;

  // --- Composite Score ---
  const score = Math.round(
    sampleFrequency * 0.40 +
    feedContinuity * 0.25 +
    mortalityReporting * 0.20 +
    temperatureData * 0.15
  );

  return {
    score,
    subscores: {
      sampleFrequency,
      feedContinuity,
      mortalityReporting,
      temperatureData,
    },
    details: {
      sampleCount,
      avgSampleInterval: sampleCount > 0 ? Math.round(daysInWater / sampleCount) : null,
      feedCoveragePercent: Math.round(feedRatio * 100),
      mortalityCoveragePercent: Math.round(mortalityRatio * 100),
      temperatureCoveragePercent: Math.round(tempRatio * 100),
    },
    warning: score < 60 ? 'Low data reliability may affect analysis accuracy.' : null,
  };
};

// ============================================================================
// MORTALITY REALITY CHECK
// ============================================================================

/**
 * Check if reported mortality is unusually low relative to biomass deviation.
 *
 * Triggers when:
 *   reported_mortality_percent < 2  AND  avg positive deviation > 10 %
 *
 * @param {number} totalMortalities – sum of all daily mortalities
 * @param {number} inputNumber      – initial stocking fish count
 * @param {Object} intelligenceData – output from calculateMissingFishIntelligence
 * @returns {Object} { triggered, mortalityPercent, advisory }
 */
export const checkMortalityReality = (totalMortalities, inputNumber, intelligenceData) => {
  if (!inputNumber || inputNumber <= 0) {
    return { triggered: false, mortalityPercent: null, advisory: null };
  }

  const mortalityPercent = Math.round((totalMortalities / inputNumber) * 100 * 10) / 10;

  // Biomass deviation threshold: either positive deviation (above model) or
  // below-model trend both suggest something is off when mortality is < 2%
  const avgPositiveDev = intelligenceData?.stats?.avgPositiveDeviation ?? 0;
  const trendClass = (intelligenceData?.trend?.classification || '').toLowerCase();
  const belowCount = intelligenceData?.stats?.belowCount ?? 0;
  const totalSamples = intelligenceData?.stats?.totalSamples ?? 0;

  const biomassDeviationExceedsThreshold =
    avgPositiveDev > 10 ||
    trendClass.includes('above') ||
    trendClass.includes('below') ||
    (totalSamples > 0 && belowCount / totalSamples > 0.5);

  const triggered = mortalityPercent < 2 && biomassDeviationExceedsThreshold;

  return {
    triggered,
    mortalityPercent,
    advisory: triggered
      ? 'Reported mortality unusually low relative to biomass trend.\n\nPossible causes:\n• Unrecorded mortality\n• Stock loss\n• Sampling bias'
      : null,
  };
};

// ============================================================================
// SAMPLING RECOMMENDATION
// ============================================================================

/**
 * Rule-based sampling recommendation using existing analysis fields.
 * No AI or heavy computation — pure conditional logic.
 *
 * Rules:
 *   confidence = "Moderate"                      → "Resample cage within 14 days."
 *   confidence = "High" AND deviation increasing  → "Immediate sampling recommended."
 *
 * @param {Object} intelligenceData – output from calculateMissingFishIntelligence
 * @returns {Object} { recommendation, priority, confidenceLevel, deviationIncreasing }
 */
export const getSamplingRecommendation = (intelligenceData) => {
  // ★ PURGED: All narrative strings removed — SSOT only
  // Returns structural data only — narrative comes from overriddenOutput.finalSamplingRecommendation
  if (!intelligenceData) {
    return { priority: null, confidenceLevel: null, deviationIncreasing: false };
  }

  const confidenceLabel = (intelligenceData.confidence?.label || '').toLowerCase();
  const pattern = intelligenceData.analyticalSignals?.pattern || {};
  const deviations = intelligenceData.deviations || [];

  const avgChange = pattern.avgChange ?? 0;
  const patternName = (pattern.pattern || '').toLowerCase();
  const deviationIncreasing =
    avgChange > 0 ||
    patternName.includes('divergence') ||
    patternName.includes('upward shift');

  let recentIncreasing = false;
  if (deviations.length >= 2) {
    const last = deviations[deviations.length - 1].deviation_percent;
    const prev = deviations[deviations.length - 2].deviation_percent;
    recentIncreasing = Math.abs(last) > Math.abs(prev);
  }

  const isIncreasing = deviationIncreasing || recentIncreasing;

  // ★ STRUCTURAL ONLY — no narrative strings
  if (confidenceLabel === 'high' && isIncreasing) {
    return {
      priority: 'urgent',
      confidenceLevel: intelligenceData.confidence?.label,
      deviationIncreasing: true,
    };
  }

  if (confidenceLabel === 'moderate') {
    return {
      priority: 'standard',
      confidenceLevel: intelligenceData.confidence?.label,
      deviationIncreasing: isIncreasing,
    };
  }

  return {
    priority: null,
    confidenceLevel: intelligenceData.confidence?.label,
    deviationIncreasing: isIncreasing,
  };
};

// ============================================================================
// HIDDEN OVERFEEDING INDICATOR
// ============================================================================

/**
 * Detect cases where feed input appears too high relative to actual growth.
 * Pure rule-based — no AI, no model reruns. Uses stored outputs only.
 *
 * Flags when MOST of these are true across multiple sample points:
 *   1. Actual growth is below model by threshold (>5%)
 *   2. Feed-implied biomass > actual biomass (feed supports more than observed)
 *   3. Observed biological FCR is worse than model FCR by threshold (>20%)
 *   4. Mismatch persists (≥2 consecutive points, not a one-point anomaly)
 *
 * @param {Object} detectionResults – output from runMissingFishDetection()
 * @param {Object} intelligenceData – output from calculateMissingFishIntelligence()
 * @returns {Object} { level, note, signals }
 */
export const detectOverfeeding = (detectionResults, intelligenceData) => {
  const none = { level: 'Low', note: null, signals: { belowModel: 0, feedExceedsActual: 0, poorFCR: 0, persistent: false, totalPoints: 0 } };

  if (!detectionResults?.available || !detectionResults.reconciliationRows) return none;

  const rows = detectionResults.reconciliationRows;
  const expectedFCR = detectionResults.inputs?.expectedFCR;
  const startingBiomassKg = detectionResults.inputs?.startingBiomassKg;

  if (!rows || rows.length < 2 || !expectedFCR || !startingBiomassKg) return none;

  const totalPoints = rows.length;
  let belowModelCount = 0;
  let feedExceedsActualCount = 0;
  let poorFCRCount = 0;
  let consecutiveMismatch = 0;
  let maxConsecutive = 0;

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    let conditionsMet = 0;

    // Condition 1: Actual growth below model by >5%
    if (r.actual_biomass_kg != null && r.model_biomass_kg != null && r.model_biomass_kg > 0) {
      const gap = (r.model_biomass_kg - r.actual_biomass_kg) / r.model_biomass_kg;
      if (gap > 0.05) {
        belowModelCount++;
        conditionsMet++;
      }
    }

    // Condition 2: Feed-implied biomass > actual biomass
    if (r.feed_implied_biomass_kg != null && r.actual_biomass_kg != null) {
      if (r.feed_implied_biomass_kg > r.actual_biomass_kg * 1.03) {
        feedExceedsActualCount++;
        conditionsMet++;
      }
    }

    // Condition 3: Observed FCR worse than model FCR by >20%
    if (r.cumulative_feed_kg != null && r.cumulative_feed_kg > 0 && r.actual_biomass_kg != null && startingBiomassKg > 0) {
      const biomassGain = r.actual_biomass_kg - startingBiomassKg;
      if (biomassGain > 0) {
        const observedFCR = r.cumulative_feed_kg / biomassGain;
        if (observedFCR > expectedFCR * 1.20) {
          poorFCRCount++;
          conditionsMet++;
        }
      }
    }

    // Condition 4: Track persistence
    if (conditionsMet >= 2) {
      consecutiveMismatch++;
      maxConsecutive = Math.max(maxConsecutive, consecutiveMismatch);
    } else {
      consecutiveMismatch = 0;
    }
  }

  const persistent = maxConsecutive >= 2;
  const signals = { belowModel: belowModelCount, feedExceedsActual: feedExceedsActualCount, poorFCR: poorFCRCount, persistent, totalPoints };

  // Also factor in trend classification from intelligence data
  const trendClass = (intelligenceData?.trend?.classification || '').toLowerCase();
  const trendBelow = trendClass.includes('below');

  // Scoring
  const ratioBelow = belowModelCount / totalPoints;
  const ratioFeedExceeds = feedExceedsActualCount / totalPoints;
  const ratioPoorFCR = poorFCRCount / totalPoints;

  let score = 0;
  if (ratioBelow > 0.5)       score += 2;
  else if (ratioBelow > 0.3)  score += 1;
  if (ratioFeedExceeds > 0.5) score += 2;
  else if (ratioFeedExceeds > 0.3) score += 1;
  if (ratioPoorFCR > 0.5)     score += 2;
  else if (ratioPoorFCR > 0.3) score += 1;
  if (persistent)              score += 2;
  if (trendBelow)              score += 1;

  let level;
  if (score >= 6) {
    level = 'High';
  } else if (score >= 3) {
    level = 'Moderate';
  } else {
    level = 'Low';
  }

  return { level, signals };
};

// ============================================================================
// GRAPH INSIGHT TEXT
// ============================================================================

/**
 * Derive a short insight sentence for display above charts.
 * Pure rule-based — uses stored analysis fields only.
 *
 * @param {Object} intelligenceData – from calculateMissingFishIntelligence()
 * @param {Object} detectionResults – from runMissingFishDetection()
 * @param {Object} overfeedingResult – from detectOverfeeding()
 * @returns {string} graph_insight_text
 */
/**
 * getGraphInsight — SSOT-driven graph insight text
 * 
 * ★ IMPORTANT: This function now returns SSOT canonical text when available.
 * It does NOT generate independent narrative conclusions.
 * 
 * @param {Object} intelligenceData - Core intelligence data
 * @param {Object} detectionResults - Detection results
 * @param {Object} overfeedingResult - Overfeeding analysis
 * @param {Object} ssot - SSOT object (source of truth)
 * @param {Object} overriddenOutput - Pre-computed SSOT override output (preferred)
 * @returns {string} Insight text from SSOT or fallback
 */
export const getGraphInsight = (intelligenceData, detectionResults, overfeedingResult, ssot = null, overriddenOutput = null) => {
  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 1: Use overriddenOutput if provided (SSOT-enforced)
  // ═══════════════════════════════════════════════════════════════════════════
  if (overriddenOutput?.finalGrowthInsight) {
    return overriddenOutput.finalGrowthInsight;
  }
  if (overriddenOutput?.finalInterpretation) {
    return overriddenOutput.finalInterpretation;
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 2: Use SSOT conclusion canonical text
  // ═══════════════════════════════════════════════════════════════════════════
  const conclusion = ssot?.conclusion;
  
  // If conflict exists, use SSOT conflict note
  if (ssot?.hasConflict || conclusion?.hasConflict) {
    return conclusion?.conflictNote || null;
  }
  
  // If SSOT has growthInsight or interpretation, use it
  if (conclusion?.growthInsight) {
    return conclusion.growthInsight;
  }
  if (conclusion?.interpretation) {
    return conclusion.interpretation;
  }
  
  // If SSOT has jumpStatusMessage (standardized wording)
  if (ssot?.jumpStatusMessage) {
    return ssot.jumpStatusMessage;
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // PRIORITY 3: Generate based on SSOT detection type (no hardcoded conclusions)
  // ═══════════════════════════════════════════════════════════════════════════
  const hasMissingFish = ssot?.hasMissingFish || false;
  const detectionStrength = ssot?.detectionStrength || 'Low';
  const feedingImpact = ssot?.feedingImpact || null;
  
  if (hasMissingFish && detectionStrength !== 'Low') {
    return null; // ★ PURGED: narrative from ssotOverride.js only
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FALLBACK: Return null — narrative comes from ssotOverride.js only
  // ═══════════════════════════════════════════════════════════════════════════
  
  return null;
};

// ============================================================================
// CONFIRMED SAMPLE JUMP — FISH LOSS DETECTION & ESTIMATION
// Sections 3-9: Detection, confirmation, biological rule, best-fit estimator
// Works for both Spreadsheet (with feed data) and Growth vs Model (no feed)
// ============================================================================

const round2 = (v) => Math.round(v * 100) / 100;
const round3 = (v) => Math.round(v * 1000) / 1000;

// Build normalised sample points from Spreadsheet reconciliation data
function buildSamplePointsFromSpreadsheet(detectionResults, modelScalingFactor = 1) {
  const rows = detectionResults.reconciliationRows;
  if (!rows) return [];
  const k = modelScalingFactor || 1;
  return rows.map((r, idx) => ({
    index: idx,
    date: r.date,
    day: r.day_index ?? idx,
    actual: r.actual_weight_g,
    model: r.model_weight_g != null ? r.model_weight_g * k : null,
    cumulativeFeedKg: r.cumulative_feed_kg ?? null,
    fishCount: r.fish_count ?? null,
  }));
}

// Build normalised sample points from Growth vs Model comparisons
function buildSamplePointsFromGrowthModel(result, modelScalingFactor = 1, excludedIndices = new Set()) {
  if (!result?.comparisons) return [];
  // Tag each comparison with its original index before sorting
  const tagged = result.comparisons.map((c, origIdx) => ({ ...c, _origIdx: origIdx }));
  // Sort by days_since_arrival to ensure strict chronological order
  const sorted = tagged.sort((a, b) => a.days_since_arrival - b.days_since_arrival);
  // Filter out manually excluded samples so jump detection ignores them
  const active = sorted.filter(c => !excludedIndices.has(c._origIdx));
  const k = modelScalingFactor || 1;

  // Build trajectory lookup from actual_growth_trajectory (the red line on the chart).
  // This is the SGR-corrected expected weight computed by the backend using model
  // growth ratios anchored at each actual sample weight — much more accurate than
  // linear extrapolation, especially over large sampling gaps.
  const trajectoryByDay = {};
  if (result.actual_growth_trajectory) {
    result.actual_growth_trajectory.forEach(p => {
      trajectoryByDay[p.day] = p.weight;
    });
  }

  return active.map((c, idx) => {
    // Get the trajectory (red line) expected weight just BEFORE this sample day.
    // Use day-1 because AT the sample day, the trajectory re-anchors to the actual
    // sample weight. We want the PROJECTED value before re-anchoring.
    let trajectoryExpected = null;
    for (let d = c.days_since_arrival - 1; d >= Math.max(0, c.days_since_arrival - 5); d--) {
      if (trajectoryByDay[d] !== undefined) {
        trajectoryExpected = trajectoryByDay[d];
        break;
      }
    }

    return {
      index: idx,
      originalIndex: c._origIdx,
      date: c.sample_date || `Sample ${c.sample_number}`,
      day: c.days_since_arrival,
      actual: c.actual_weight_g,
      model: c.model_weight_g * k,
      trajectoryExpected,  // Red line expected weight just before this sample
      cumulativeFeedKg: null,
      fishCount: null,
    };
  });
}

// Section 6-7: Feed-based Best-Fit (Spreadsheet mode)
function runFeedBasedBestFit(detectionResults, samplePoints, bestEvent, originalFishNumber) {
  const startingBiomassKg = detectionResults.inputs?.startingBiomassKg || 0;
  const expectedFCR = detectionResults.inputs?.expectedFCR || 1.6;

  const jumpPt = samplePoints[bestEvent.jumpIndex];
  const confirmPt = samplePoints[bestEvent.confirmIndex];

  if (!jumpPt || !confirmPt) return { best_fit_fish_number: null, estimated_missing_fish: null, fit_error_score: null };
  if (jumpPt.cumulativeFeedKg == null || confirmPt.cumulativeFeedKg == null) {
    return runTrajectoryBasedBestFit(samplePoints, bestEvent, originalFishNumber);
  }

  const feedBiomassAtJump = startingBiomassKg + jumpPt.cumulativeFeedKg / expectedFCR;
  const feedBiomassAtConfirm = startingBiomassKg + confirmPt.cumulativeFeedKg / expectedFCR;

  const observedJump = jumpPt.actual;
  const observedConfirm = confirmPt.actual;

  // Phase 1: Coarse search — 5% steps from 100% down to 40%
  const minN = Math.max(1, Math.round(originalFishNumber * 0.40));
  const coarseStep = Math.max(1, Math.round(originalFishNumber * 0.05));

  let bestN = originalFishNumber;
  let bestError = Infinity;

  for (let N = originalFishNumber; N >= minN; N -= coarseStep) {
    const predJump = (feedBiomassAtJump * 1000) / N;
    const predConfirm = (feedBiomassAtConfirm * 1000) / N;
    const err = Math.abs(predJump - observedJump) + Math.abs(predConfirm - observedConfirm);
    if (err < bestError) { bestError = err; bestN = N; }
  }

  // Phase 2: Fine search — 1% steps around best coarse candidate
  const fineStep = Math.max(1, Math.round(originalFishNumber * 0.01));
  const fineMin = Math.max(1, bestN - coarseStep);
  const fineMax = Math.min(originalFishNumber, bestN + coarseStep);

  for (let N = fineMax; N >= fineMin; N -= fineStep) {
    const predJump = (feedBiomassAtJump * 1000) / N;
    const predConfirm = (feedBiomassAtConfirm * 1000) / N;
    const err = Math.abs(predJump - observedJump) + Math.abs(predConfirm - observedConfirm);
    if (err < bestError) { bestError = err; bestN = N; }
  }

  const estimated_missing = originalFishNumber - bestN;

  return {
    best_fit_fish_number: bestN,
    estimated_missing_fish: estimated_missing > 0 ? estimated_missing : 0,
    fit_error_score: round2(bestError),
  };
}

// Section 6-7: Trajectory-based Best-Fit (Growth vs Model / fallback)
function runTrajectoryBasedBestFit(samplePoints, bestEvent, originalFishNumber) {
  const EMPTY = {
    best_fit_fish_number: null, estimated_missing_fish: null, fit_error_score: null,
    trajectory_only_missing: null, estimate_range_low: null, estimate_range_high: null,
    estimation_quality: null, gap_days: null, trajectory_weight: null,
    blended_expected: null, trajectory_expected: null, model_expected: null,
  };
  const jumpPt = samplePoints[bestEvent.jumpIndex];
  if (!jumpPt || jumpPt.actual <= 0 || bestEvent.expectedWeightAtJump <= 0) return EMPTY;

  const preJumpPt = samplePoints[bestEvent.preJumpIndex2];
  const gapDays = jumpPt.day - (preJumpPt?.day || 0);

  // ── The detection loop already blended expectedWeightAtJump ─────────────
  // bestEvent.expectedWeightAtJump = gap-weighted blend of trajectory + model
  // bestEvent.trajectoryExpectedAtJump = pure trajectory projection
  // bestEvent.gapTrajectoryWeight = the weight given to trajectory (0.3–1.0)
  const blendedExpected = bestEvent.expectedWeightAtJump;
  const trajectoryExpected = bestEvent.trajectoryExpectedAtJump || bestEvent.expectedWeightAtJump;
  const modelExpected = jumpPt.model; // already scaled by modelScalingFactor
  const trajectoryWeight = bestEvent.gapTrajectoryWeight ?? 1.0;

  // ── PRIMARY ESTIMATE (blended — conservative) ──────────────────────────
  const blendedRatio = blendedExpected / jumpPt.actual;
  let bestN = Math.round(originalFishNumber * blendedRatio);

  // ── TRAJECTORY-ONLY ESTIMATE (aggressive upper bound) ──────────────────
  const trajRatio = trajectoryExpected / jumpPt.actual;
  let trajOnlyN = Math.round(originalFishNumber * trajRatio);

  // ── CONFIRMATION REFINEMENT (applied to both estimates) ────────────────
  if (bestEvent.confirmIndex != null && samplePoints[bestEvent.confirmIndex]) {
    const confirmPt = samplePoints[bestEvent.confirmIndex];
    const preJump2 = samplePoints[bestEvent.preJumpIndex2];
    const preJump1 = samplePoints[bestEvent.preJumpIndex1];
    if (preJump2 && preJump1 && confirmPt.actual > 0 && preJump2.actual > 0) {
      const dailyG = (preJump2.actual - preJump1.actual) / Math.max(1, preJump2.day - preJump1.day);
      const trajExpectedConfirm = preJump2.actual + dailyG * (confirmPt.day - preJump2.day);
      if (trajExpectedConfirm > 0) {
        // Trajectory-only confirmation
        const trajRatioC = trajExpectedConfirm / confirmPt.actual;
        trajOnlyN = Math.round((trajOnlyN + Math.round(originalFishNumber * trajRatioC)) / 2);

        // Blended confirmation (same gap-weighted blend at confirmation day)
        const confirmModel = samplePoints[bestEvent.confirmIndex].model;
        const hasModel = confirmModel > 0;
        const blendedConfirm = hasModel
          ? trajectoryWeight * trajExpectedConfirm + (1 - trajectoryWeight) * confirmModel
          : trajExpectedConfirm;
        const blendedRatioC = blendedConfirm / confirmPt.actual;
        bestN = Math.round((bestN + Math.round(originalFishNumber * blendedRatioC)) / 2);
      }
    }
  }

  bestN = Math.max(1, Math.min(originalFishNumber, bestN));
  trajOnlyN = Math.max(1, Math.min(originalFishNumber, trajOnlyN));

  const blendedMissing = Math.max(0, originalFishNumber - bestN);
  const trajOnlyMissing = Math.max(0, originalFishNumber - trajOnlyN);

  // ── FIT ERROR & QUALITY ASSESSMENT ─────────────────────────────────────
  const fitError = Math.abs(jumpPt.actual - blendedExpected);
  const fitErrorPct = blendedExpected > 0 ? (fitError / blendedExpected) * 100 : 0;

  let estimationQuality = 'good';
  if (gapDays > 120 || fitErrorPct > 30) estimationQuality = 'poor';
  else if (gapDays > 60 || fitErrorPct > 15) estimationQuality = 'moderate';

  // ── ESTIMATE RANGE ─────────────────────────────────────────────────────
  const rangeLow = Math.min(blendedMissing, trajOnlyMissing);
  const rangeHigh = Math.max(blendedMissing, trajOnlyMissing);

  return {
    best_fit_fish_number: bestN,
    estimated_missing_fish: blendedMissing,
    fit_error_score: round2(fitError),
    trajectory_only_fish: trajOnlyN,
    trajectory_only_missing: trajOnlyMissing,
    estimate_range_low: rangeLow,
    estimate_range_high: rangeHigh,
    estimation_quality: estimationQuality,
    gap_days: gapDays,
    trajectory_weight: round2(trajectoryWeight),
    blended_expected: round2(blendedExpected),
    trajectory_expected: round2(trajectoryExpected),
    model_expected: round2(modelExpected),
  };
}

// Section 8: Confidence Scoring
// Works for both Spreadsheet (with feed/reconciliation data) and Growth vs Model
function scoreJumpConfidence(bestEvent, detectionResults, intelligenceData, overfeedingResult, originalFishNumber) {
  let supportingSignals = 0;
  let reducingSignals = 0;

  const overfeedLevel = (overfeedingResult?.level || 'low').toLowerCase();
  if (overfeedLevel === 'moderate' || overfeedLevel === 'high') supportingSignals++;

  // Spreadsheet-specific signals (require feed/reconciliation data)
  if (detectionResults?.inputs?.expectedFCR && detectionResults?.inputs?.startingBiomassKg) {
    const rows = detectionResults.reconciliationRows || [];
    const jumpRow = rows[bestEvent.jumpIndex];
    if (jumpRow?.cumulative_feed_kg > 0 && jumpRow?.actual_biomass_kg > detectionResults.inputs.startingBiomassKg) {
      const obsF = jumpRow.cumulative_feed_kg / (jumpRow.actual_biomass_kg - detectionResults.inputs.startingBiomassKg);
      if (obsF > detectionResults.inputs.expectedFCR * 1.15) supportingSignals++;
    }
    if (jumpRow?.feed_implied_biomass_kg != null && jumpRow?.actual_biomass_kg != null) {
      if (jumpRow.feed_implied_biomass_kg < jumpRow.actual_biomass_kg * 0.95) supportingSignals++;
    }
  }

  if (originalFishNumber && originalFishNumber > 0 && detectionResults?.reconciliationRows) {
    const lastRow = detectionResults.reconciliationRows[detectionResults.reconciliationRows.length - 1];
    const totalMort = lastRow?.cumulative_mortality || 0;
    if ((totalMort / originalFishNumber) * 100 < 2) supportingSignals++;
  }

  // Universal signals (work for both modes)
  const trendClass = (intelligenceData?.trend?.classification || '').toLowerCase();
  if (trendClass.includes('above') || trendClass.includes('diverg')) supportingSignals++;

  // Jump above model weight — strong corroborating signal
  if (bestEvent.jumpAboveModel) supportingSignals++;

  // Strong jump ratio (>15% deviation from trajectory) — additional confidence
  if (bestEvent.jumpRatio >= 1.15) supportingSignals++;

  // ── Biological credibility modifiers ────────────────────────────────
  // Weak/stressed baselines reduce confidence rather than block detection.
  if (bestEvent.baselineFlag === 'stressed') reducingSignals += 2;
  else if (bestEvent.baselineFlag === 'weak') reducingSignals += 1;
  else if (bestEvent.baselineFlag === 'above_model') reducingSignals += 1;

  const netSignals = supportingSignals - reducingSignals;
  if (bestEvent.confirmed && netSignals >= 2) return 'High';
  if (bestEvent.confirmed && netSignals >= 1) return 'Moderate';
  if (bestEvent.confirmed) return 'Low';
  return 'Low';
}

/**
 * Detect confirmed sample jump events and estimate fish loss.
 * Full implementation of Sections 3-9.
 *
 * @param {Object} detectionResults – from runMissingFishDetection() (Spreadsheet)
 * @param {Object} intelligenceData – from calculateMissingFishIntelligence()
 * @param {Object} overfeedingResult – from detectOverfeeding()
 * @param {Object} [growthModelResult] – from Growth vs Model analysis (optional)
 * @param {number} [modelScalingFactor=1] – Global best-fit scaling factor for adjusted model baseline
 * @returns {Object} Full jump detection result per Section 9
 */
export const detectConfirmedSampleJump = (detectionResults, intelligenceData, overfeedingResult, growthModelResult = null, modelScalingFactor = 1, excludedIndices = new Set()) => {
  const empty = {
    potential_jump_event: false,
    jump_event_detected: false,
    jump_event_confirmed: false,
    jump_confirmation_pending: false,
    jump_sample_index: null,
    jump_sample_date: null,
    confirmation_sample_index: null,
    confirmation_sample_date: null,
    probable_loss_window_start: null,
    probable_loss_window_end: null,
    jump_ratio: null,
    best_fit_fish_number: null,
    estimated_missing_fish: null,
    fit_error_score: null,
    trajectory_jump_confidence: null,
    jump_event_note: null,
    suspect_samples: [],
    events: [],
    // Legacy compat
    trajectory_jump_detected: false,
    trajectory_jump_confirmed: false,
    trajectory_loss_confidence: null,
    expected_weight_at_jump_from_prior_trajectory: null,
    trajectory_based_missing_fish_estimate: null,
    adjusted_fish_count: null,
    note: null,
  };

  const isSpreadsheetMode = detectionResults?.available && detectionResults.reconciliationRows;
  const isGrowthModelMode = !isSpreadsheetMode && growthModelResult?.comparisons?.length >= 3;

  if (!isSpreadsheetMode && !isGrowthModelMode) return empty;

  const samplePoints = isSpreadsheetMode
    ? buildSamplePointsFromSpreadsheet(detectionResults, modelScalingFactor)
    : buildSamplePointsFromGrowthModel(growthModelResult, modelScalingFactor, excludedIndices);

  if (samplePoints.length < 3) return empty;

  const originalFishNumber = isSpreadsheetMode
    ? detectionResults.inputs?.inputNumber
    : growthModelResult?.starting_fish_number;

  // Section 3: Detection
  // ═══════════════════════════════════════════════════════════════════════════
  // DETECTION TRIGGER (per specification):
  // A jump is detected when sample weight is >5% ABOVE the expected TRAJECTORY
  // (red line) — the extrapolation of the fish's established growth path.
  // A jump is ONLY an upward shift (never downward).
  // ═══════════════════════════════════════════════════════════════════════════
  const JUMP_THRESHOLD_VS_TRAJECTORY = 1.05;  // >5% above TRAJECTORY weight (CRITICAL GATE)
  const events = [];
  const suspectSamples = [];

  for (let i = 2; i < samplePoints.length; i++) {
    const p0 = samplePoints[i - 2];
    const p1 = samplePoints[i - 1];
    const pJump = samplePoints[i];

    if (p0.actual == null || p1.actual == null || pJump.actual == null) continue;
    if (p1.day <= p0.day) continue;

    // Calculate trajectory FIRST (needed for primary gate)
    const daySpan01 = p1.day - p0.day;
    const dailyGrowth = (p1.actual - p0.actual) / daySpan01;
    const daysToJump = pJump.day - p1.day;
    if (daysToJump <= 0) continue;

    // ═══════════════════════════════════════════════════════════════════════════
    // TRAJECTORY EXPECTED WEIGHT
    //
    // PRIMARY: Use the backend's actual_growth_trajectory (red line on the chart)
    // — SGR-corrected expected weight derived from model growth ratios, anchored
    // at the prior actual sample weight. This correctly accounts for exponential
    // fish growth between samples and is reliable even for large sampling gaps.
    //
    // FALLBACK: Linear extrapolation from last 2 samples (used when backend
    // trajectory data isn't available, e.g. spreadsheet mode)
    // ═══════════════════════════════════════════════════════════════════════════

    // Linear trajectory (fallback for when chart trajectory isn't available)
    const rawExpected = p1.actual + dailyGrowth * daysToJump;
    const linearTrajectoryAtJump = Math.max(rawExpected, p1.actual);

    // Use chart trajectory (SGR-corrected red line) if available
    const chartTrajectoryAtJump = pJump.trajectoryExpected;
    const trajectoryExpectedAtJump = (chartTrajectoryAtJump != null && chartTrajectoryAtJump > 0)
      ? chartTrajectoryAtJump
      : linearTrajectoryAtJump;
    if (trajectoryExpectedAtJump <= 0) continue;

    // ═══════════════════════════════════════════════════════════════════════════
    // CRITICAL GATE: Jump MUST be >5% above TRAJECTORY (red line)
    // A jump is ONLY an upward shift from the fish's established growth path.
    // If actual weight is at or below trajectory, this is normal growth.
    // ═══════════════════════════════════════════════════════════════════════════
    const jumpAboveTrajectory = pJump.actual > trajectoryExpectedAtJump * JUMP_THRESHOLD_VS_TRAJECTORY;

    // If sample is NOT >5% above trajectory, skip jump detection entirely
    if (!jumpAboveTrajectory) continue;

    // Model weight for secondary scoring/metadata (not used for gate)
    const modelWeightAtJump = pJump.model;
    const jumpAboveModel = modelWeightAtJump > 0 && pJump.actual > modelWeightAtJump * 1.05;

    // ── GAP-WEIGHTED BLEND with adjusted model baseline ──────────────────
    // Short gaps: trust trajectory. Long gaps: lean on the adjusted model.
    // Used for fish-number estimation, NOT for the detection gate.
    const hasModelAtJump = modelWeightAtJump > 0;
    const gapTrajectoryWeight = hasModelAtJump
      ? Math.max(0.30, Math.min(1.0, 1.0 - (daysToJump - 30) / 120))
      : 1.0;
    const expectedWeightAtJump = hasModelAtJump
      ? gapTrajectoryWeight * trajectoryExpectedAtJump + (1 - gapTrajectoryWeight) * modelWeightAtJump
      : trajectoryExpectedAtJump;
    if (expectedWeightAtJump <= 0) continue;

    // Trajectory-based jump ratio (PRIMARY metric — used for gate and scoring)
    const jumpRatioVsTrajectory = pJump.actual / trajectoryExpectedAtJump;
    // Model-based deviation (secondary metadata for scoring)
    const jumpRatioVsModel = modelWeightAtJump > 0 ? pJump.actual / modelWeightAtJump : jumpRatioVsTrajectory;

    const p0vsModel = p0.model > 0 ? (p0.actual - p0.model) / p0.model : 0;
    const p1vsModel = p1.model > 0 ? (p1.actual - p1.model) / p1.model : 0;

    // ═══════════════════════════════════════════════════════════════════════════
    // EDGE CASE: If prior growth was BELOW model, bias toward environmental
    // recovery / compensatory growth interpretation, NOT missing fish.
    // When using SGR-corrected trajectory: the trajectory already accounts for
    // the fish's actual growth pattern, so the main 5% gate is sufficient.
    // When using linear fallback: require stronger evidence (15%) because
    // linear extrapolation can inflate ratios over long sampling gaps.
    // ═══════════════════════════════════════════════════════════════════════════
    const usingSGRTrajectory = (chartTrajectoryAtJump != null && chartTrajectoryAtJump > 0);
    const priorBelowModel = p0vsModel < -0.05 && p1vsModel < -0.05;
    if (priorBelowModel && !usingSGRTrajectory) {
      // Linear trajectory fallback — require a much stronger signal (>15%)
      // to distinguish missing fish from compensatory growth over long gaps
      const strongJumpAboveTrajectory = jumpRatioVsTrajectory >= 1.15;
      if (!strongJumpAboveTrajectory) {
        // Likely compensatory growth, not a fish loss event — skip
        continue;
      }
    }

    // ── Biological credibility scoring ───────────────────────────────────
    const modelDailyGrowth = p1.model > p0.model && daySpan01 > 0
      ? (p1.model - p0.model) / daySpan01
      : 0;
    const growthRateRatio = modelDailyGrowth > 0 ? dailyGrowth / modelDailyGrowth : 1;
    const bothDeeplyBelow = p0vsModel < -0.20 && p1vsModel < -0.20;

    let credibility = 0;
    let baselineFlag = 'normal';  // 'normal' | 'weak' | 'stressed' | 'above_model'

    if (dailyGrowth <= 0) {
      // Negative/stagnant prior trajectory — could be stress recovery.
      // Penalise credibility but do NOT eliminate the event.
      credibility -= 25;
      baselineFlag = 'stressed';
    } else if (growthRateRatio < 0.30 && bothDeeplyBelow) {
      // Very slow growth from a deeply suppressed baseline.
      credibility -= 15;
      baselineFlag = 'weak';
    } else if (p0vsModel > 0.10 && p1vsModel > 0.10) {
      // Both prior samples already >10% above model — the fish are already
      // outperforming.  A subsequent upward shift may be smooth continuation
      // rather than a loss event, so reduce credibility.  But a large,
      // confirmed discontinuity can still overcome this penalty.
      credibility -= 15;
      baselineFlag = 'above_model';
    } else {
      // Healthy prior trajectory — bonus proportional to model alignment
      credibility += Math.min(growthRateRatio, 1.5) * 20;  // 0–30 pts
    }

    // Jump above model is the hallmark fish-loss pattern (already gated, so this is bonus)
    credibility += 30;  // We already passed the >5% above model gate
    // Prior samples near model (not deeply stressed)
    const priorHealth = 1 - Math.min(Math.abs(p1vsModel), 0.5) / 0.5;
    credibility += priorHealth * 20;  // 0–20 pts for healthy baseline
    // Jump ratio magnitude vs model (scaled, capped at 20)
    credibility += Math.min((jumpRatioVsModel - 1) * 100, 20);

    const evt = {
      jumpIndex: i,
      jumpDate: pJump.date,
      jumpDay: pJump.day,
      preJumpIndex1: i - 2,
      preJumpIndex2: i - 1,
      preJumpDate1: p0.date,
      preJumpDate2: p1.date,
      preJumpDay1: p0.day,
      preJumpDay2: p1.day,
      expectedWeightAtJump: round2(expectedWeightAtJump),
      trajectoryExpectedAtJump: round2(trajectoryExpectedAtJump),
      gapTrajectoryWeight: round2(gapTrajectoryWeight),
      observedWeightAtJump: round2(pJump.actual),
      modelWeightAtJump: round2(modelWeightAtJump),
      jumpAboveModel,
      jumpAboveTrajectory,
      jumpRatio: round3(jumpRatioVsTrajectory),  // Report vs TRAJECTORY ratio (primary)
      jumpRatioVsTrajectory: round3(jumpRatioVsTrajectory),
      jumpRatioVsModel: round3(jumpRatioVsModel),  // Secondary metric
      credibility: round2(credibility),
      baselineFlag,
      confirmed: false,
      reverted: false,
      confirmIndex: null,
      confirmDate: null,
      confirmDay: null,
      confirmWeight: null,
    };

    // Section 4: Confirmation Rule — Sustained Trajectory Check
    //
    // A jump is confirmed if the next sample is ≥ the expected growth
    // trajectory (red line) at that day — i.e., not reverting below model.
    //
    // Algorithm:
    //   1. Collect up to 4 post-jump samples.
    //   2. PRIMARY: Check if actual ≥ chart trajectory (SGR-corrected) at each day.
    //   3. FALLBACK: Distance-based check + >5% above linear pre-jump trajectory.
    //   4. Single sustaining sample = confirmed (biological event is real).
    //   5. If sample reverts below expected trajectory → sampling error.
    //   6. Multiple samples: majority vote determines confirmation.

    // ── Post-jump trajectory: parallel-shifted pre-jump growth ──────────
    // In a real missing-fish event, fish continue growing at the NORMAL rate
    // after the loss — only the baseline shifts up (fewer fish = higher avg weight).
    // The post-jump trajectory is therefore the pre-jump trajectory shifted up
    // by the jump amount, NOT an extrapolation of the spike growth rate.
    const jumpShift = pJump.actual - expectedWeightAtJump;

    // Gather post-jump samples (up to 4)
    const postJumpWindow = [];
    for (let k = i + 1; k < samplePoints.length && k <= i + 4; k++) {
      if (samplePoints[k].actual != null && samplePoints[k].day > pJump.day) {
        postJumpWindow.push({ idx: k, pt: samplePoints[k] });
      }
    }

    if (postJumpWindow.length > 0) {
      let sustainCount = 0;
      let revertCount = 0;

      for (const { pt } of postJumpWindow) {
        const daysFromP1 = pt.day - p1.day;

        // Pre-jump trajectory projection (clamped to at least p1 weight)
        const preJumpProj = Math.max(p1.actual + dailyGrowth * daysFromP1, p1.actual);
        // Post-jump trajectory: pre-jump trajectory shifted up by the jump amount
        // Models: normal growth continues, just at a permanently higher baseline
        const postJumpProj = preJumpProj + jumpShift;

        const distToPre = Math.abs(pt.actual - preJumpProj);
        const distToPost = Math.abs(pt.actual - postJumpProj);

        // ═══════════════════════════════════════════════════════════════════
        // CONFIRMATION REQUIREMENT:
        // A jump is confirmed if the next sample is ≥ model expectation
        // (the expected trajectory at that day). Not necessarily +5% above —
        // just not reverting below the expected growth path.
        //
        // PRIMARY: Use the chart trajectory (red line) at the confirming
        // sample day. This is the SGR-corrected expected weight.
        // FALLBACK: Use distance-based + >5% above linear pre-jump trajectory.
        // ═══════════════════════════════════════════════════════════════════
        const hasChartTrajectory = pt.trajectoryExpected != null && pt.trajectoryExpected > 0;

        let sustains;
        if (hasChartTrajectory) {
          // Chart trajectory available: confirm if actual ≥ expected trajectory
          sustains = pt.actual >= pt.trajectoryExpected;
        } else {
          // Fallback: distance-based check + >5% above linear pre-jump trajectory
          const confirmStillAboveTrajectory = preJumpProj > 0 && pt.actual > preJumpProj * 1.05;
          sustains = distToPost <= distToPre && confirmStillAboveTrajectory;
        }

        if (sustains) {
          sustainCount++;
        } else {
          revertCount++;
        }
      }

      // Strong signal flag: >15% above trajectory allows confirmation with fewer samples
      const isStrongSignal = jumpRatioVsTrajectory >= 1.15;

      if (postJumpWindow.length === 1) {
        // Single post-jump sample
        if (revertCount > 0) {
          // Single sample clearly reverts → sampling error
          const { pt } = postJumpWindow[0];
          evt.reverted = true;
          suspectSamples.push({
            sampleIndex: i,
            sampleDate: pJump.date,
            sampleDay: pJump.day,
            sampleWeight: round2(pJump.actual),
            expectedWeight: round2(expectedWeightAtJump),
            reversionSampleDate: pt.date,
            reversionWeight: round2(pt.actual),
            flag: 'Unconfirmed jump — sampling error',
            reason: 'Jump followed by reversion to the pre-jump trajectory.',
          });
        } else {
          // Single sample sustains — confirm the jump.
          // With chart trajectory, sustaining = actual ≥ expected trajectory
          // at the confirming sample day, which is sufficient evidence.
          const { pt, idx } = postJumpWindow[0];
          evt.confirmed = true;
          evt.confirmIndex = idx;
          evt.confirmDate = pt.date;
          evt.confirmDay = pt.day;
          evt.confirmWeight = round2(pt.actual);
          evt.credibility += isStrongSignal ? 50 : 40;
        }
      } else {
        // Multiple post-jump samples: use majority vote
        if (sustainCount > revertCount) {
          // Majority sustains → confirmed fish loss
          const firstSustaining = postJumpWindow[0];
          evt.confirmed = true;
          evt.confirmIndex = firstSustaining.idx;
          evt.confirmDate = firstSustaining.pt.date;
          evt.confirmDay = firstSustaining.pt.day;
          evt.confirmWeight = round2(firstSustaining.pt.actual);
          evt.credibility += sustainCount >= 2 ? 50 : 40;
        } else {
          // Majority reverted or insufficient sustaining samples → sampling error
          evt.reverted = true;
          suspectSamples.push({
            sampleIndex: i,
            sampleDate: pJump.date,
            sampleDay: pJump.day,
            sampleWeight: round2(pJump.actual),
            expectedWeight: round2(expectedWeightAtJump),
            flag: 'Unconfirmed jump — sampling error',
            reason: `Jump not sustained: ${revertCount} of ${postJumpWindow.length} subsequent samples reverted to the pre-jump trajectory.`,
          });
        }
      }
    }
    // If no post-jump samples available, event stays unconfirmed (pending)

    events.push(evt);
  }

  if (events.length === 0) return { ...empty, suspect_samples: suspectSamples };

  // Exclude reverted events (classified as sampling errors) from fish-loss consideration
  const validEvents = events.filter(e => !e.reverted);

  // If all events were reverted → no fish loss, return suspect samples only
  if (validEvents.length === 0) {
    return {
      ...empty,
      suspect_samples: suspectSamples,
      events,
      jump_event_note: suspectSamples.length > 0
        ? `${suspectSamples.length} sample jump(s) detected but each was followed by a reversion to the pre-jump trajectory. These are classified as probable sampling errors, not fish loss events.`
        : null,
    };
  }

  // Pick the most biologically credible event, not just the strongest ratio.
  // Credibility score incorporates: confirmation status, baseline health,
  // whether the jump exceeds the model, and ratio magnitude.  When only one
  // event exists it's always used regardless of credibility.
  const confirmedEvents = validEvents.filter(e => e.confirmed);
  const bestEvent = confirmedEvents.length > 0
    ? confirmedEvents.reduce((a, b) => b.credibility > a.credibility ? b : a, confirmedEvents[0])
    : validEvents.reduce((a, b) => b.credibility > a.credibility ? b : a, validEvents[0]);

  // Section 4: Pending confirmation
  if (!bestEvent.confirmed) {
    return {
      ...empty,
      potential_jump_event: true,
      jump_event_detected: true,
      jump_event_confirmed: false,
      jump_confirmation_pending: true,
      jump_sample_index: bestEvent.jumpIndex,
      jump_sample_date: bestEvent.jumpDate,
      jump_sample_day: bestEvent.jumpDay,
      jump_ratio: bestEvent.jumpRatio,
      jump_above_model: bestEvent.jumpAboveModel,
      jump_above_trajectory: bestEvent.jumpAboveTrajectory,
      probable_loss_window_start: bestEvent.preJumpDate2,
      probable_loss_window_end: bestEvent.jumpDate,
      loss_window_start_day: bestEvent.preJumpDay2,
      loss_window_end_day: bestEvent.jumpDay,
      jump_event_note: 'Potential sample jump detected. The observed weight is significantly higher than projected from the prior trajectory. Waiting for the next sample to confirm.',
      trajectory_jump_detected: true,
      note: 'Potential sample jump detected. Waiting for the next sample to confirm.',
      suspect_samples: suspectSamples,
      events,
    };
  }

  // Section 6-7: Best-Fit Fish Number Estimator
  let bestFitResult = { best_fit_fish_number: null, estimated_missing_fish: null, fit_error_score: null };
  if (originalFishNumber && originalFishNumber > 0) {
    bestFitResult = isSpreadsheetMode
      ? runFeedBasedBestFit(detectionResults, samplePoints, bestEvent, originalFishNumber)
      : runTrajectoryBasedBestFit(samplePoints, bestEvent, originalFishNumber);
  }

  // Section 8: Confidence
  const confidence = scoreJumpConfidence(bestEvent, detectionResults, intelligenceData, overfeedingResult, originalFishNumber);

  // Confirmed jump → fish-loss interpretation, with biological context about
  // baseline quality.  Weak baselines produce lower confidence but don't
  // invalidate the confirmed signal.
  // ★ PURGED: noteText narrative deleted — all narrative from ssotOverride.js only
  const noteText = null;

  return {
    potential_jump_event: true,
    jump_event_detected: true,
    jump_event_confirmed: true,
    jump_confirmation_pending: false,
    jump_sample_index: bestEvent.jumpIndex,
    jump_sample_date: bestEvent.jumpDate,
    jump_sample_day: bestEvent.jumpDay,
    confirmation_sample_index: bestEvent.confirmIndex,
    confirmation_sample_date: bestEvent.confirmDate,
    confirmation_sample_day: bestEvent.confirmDay,
    probable_loss_window_start: bestEvent.preJumpDate2,
    probable_loss_window_end: bestEvent.jumpDate,
    loss_window_start_day: bestEvent.preJumpDay2,
    loss_window_end_day: bestEvent.jumpDay,
    jump_ratio: bestEvent.jumpRatio,
    jump_above_model: bestEvent.jumpAboveModel,
    jump_above_trajectory: bestEvent.jumpAboveTrajectory,
    best_fit_fish_number: bestFitResult.best_fit_fish_number,
    estimated_missing_fish: bestFitResult.estimated_missing_fish,
    fit_error_score: bestFitResult.fit_error_score,
    // Conservative estimation fields
    trajectory_only_missing: bestFitResult.trajectory_only_missing,
    trajectory_only_fish: bestFitResult.trajectory_only_fish,
    estimate_range_low: bestFitResult.estimate_range_low,
    estimate_range_high: bestFitResult.estimate_range_high,
    estimation_quality: bestFitResult.estimation_quality,
    gap_days: bestFitResult.gap_days,
    trajectory_weight: bestFitResult.trajectory_weight,
    blended_expected: bestFitResult.blended_expected,
    trajectory_expected: bestFitResult.trajectory_expected,
    model_expected: bestFitResult.model_expected,
    trajectory_jump_confidence: confidence,
    baseline_flag: bestEvent.baselineFlag,
    jump_event_note: noteText,
    suspect_samples: suspectSamples,
    events,
    // Legacy compat
    trajectory_jump_detected: true,
    trajectory_jump_confirmed: true,
    trajectory_loss_confidence: confidence,
    expected_weight_at_jump_from_prior_trajectory: bestEvent.expectedWeightAtJump,
    trajectory_based_missing_fish_estimate: bestFitResult.trajectory_only_missing ?? bestFitResult.estimated_missing_fish,
    adjusted_fish_count: bestFitResult.best_fit_fish_number,
    note: noteText,
  };
};

// Legacy wrapper for existing Spreadsheet-only calls
export const detectTrajectoryJump = (detectionResults, intelligenceData, overfeedingResult) => {
  return detectConfirmedSampleJump(detectionResults, intelligenceData, overfeedingResult, null);
};

/**
 * Compute adjusted actual weight curve for a given assumed fish number.
 * Uses feed-implied biomass: weight = (startingBiomass + cumFeed/FCR) * 1000 / N
 * @param {Array} dailyRows - daily analysis rows with cumulative_feed_kg
 * @param {number} assumedN - assumed fish number
 * @param {number} startingBiomassKg - starting total biomass in kg
 * @param {number} expectedFCR - expected feed conversion ratio
 * @returns {Array} array of {day_index, adjusted_weight} values
 */
/**
 * Compute the "best-fit" adjusted-actual curve — the feed-derived weight
 * trajectory implied when the fish count is `assumedN` instead of the
 * reported count. Anchors at sample points so the line visibly passes
 * through (or immediately next to) the red sample dots on the Growth vs
 * Model chart. Between samples the curve is interpolated from the cumulative
 * feed / expectedFCR biomass math, then linearly re-scaled so the endpoints
 * match the bounding samples exactly. Outside the sample range the curve
 * falls back to the plain feed-biomass equation.
 *
 * @param {Array} dailyRows                – daily analysis rows
 * @param {number} assumedN                – fish count used for the curve
 * @param {number} startingBiomassKg       – biomass on day 0
 * @param {number} expectedFCR             – model FCR used in feed→biomass
 */
export const computeAdjustedActualCurve = (dailyRows, assumedN, startingBiomassKg, expectedFCR) => {
  if (!dailyRows?.length || !assumedN || assumedN <= 0 || !startingBiomassKg || !expectedFCR) return [];

  // 1. Raw feed-biomass curve (model path — no sample anchoring).
  const rawCurve = dailyRows.map((row) => {
    const cumFeed = row.cumulative_feed_kg || 0;
    const feedBiomass = startingBiomassKg + cumFeed / expectedFCR;
    return (feedBiomass * 1000) / assumedN;
  });

  // 2. Identify sample points (is_sample rows with real weights). These
  //    are the anchors the user wants the curve to pass through.
  const samples = [];
  dailyRows.forEach((row, idx) => {
    if (row.is_sample && row.actual_weight_g > 0) {
      samples.push({ idx, weight: row.actual_weight_g });
    }
  });

  // No samples → return the plain feed-biomass curve (matches old behaviour).
  if (samples.length === 0) {
    return dailyRows.map((row, i) => ({
      day_index: row.day_index,
      date: row.date,
      adjusted_weight: Math.round(rawCurve[i] * 100) / 100,
    }));
  }

  // 3. Within each sample-bounded segment, re-scale the raw curve linearly
  //    so the endpoints match the sample weights exactly. This preserves
  //    the shape of the feed-biomass curve between samples while forcing
  //    the line to pass through every sample anchor.
  const adjusted = new Array(dailyRows.length);

  // Segment 0: day 0 → first sample
  const firstSample = samples[0];
  const startWeight = (startingBiomassKg * 1000) / assumedN;
  const rawAtFirstSample = rawCurve[firstSample.idx] || firstSample.weight;
  for (let i = 0; i <= firstSample.idx; i++) {
    const rawDelta = rawCurve[i] - startWeight;
    const rawRange = rawAtFirstSample - startWeight;
    const frac = Math.abs(rawRange) > 1e-6 ? rawDelta / rawRange : (i / Math.max(firstSample.idx, 1));
    adjusted[i] = startWeight + frac * (firstSample.weight - startWeight);
  }

  // Interior segments: sample[k] → sample[k+1]
  for (let s = 0; s < samples.length - 1; s++) {
    const a = samples[s];
    const b = samples[s + 1];
    const rawA = rawCurve[a.idx];
    const rawB = rawCurve[b.idx];
    const rawRange = rawB - rawA;
    for (let i = a.idx + 1; i <= b.idx; i++) {
      const rawDelta = rawCurve[i] - rawA;
      const frac = Math.abs(rawRange) > 1e-6
        ? rawDelta / rawRange
        : (i - a.idx) / Math.max(b.idx - a.idx, 1);
      adjusted[i] = a.weight + frac * (b.weight - a.weight);
    }
  }

  // Tail segment: last sample → end — continue along the feed-biomass curve
  // by preserving the offset from the last sample anchor.
  const last = samples[samples.length - 1];
  const rawAtLast = rawCurve[last.idx];
  const offset = last.weight - rawAtLast;
  for (let i = last.idx + 1; i < dailyRows.length; i++) {
    adjusted[i] = rawCurve[i] + offset;
  }

  return dailyRows.map((row, i) => ({
    day_index: row.day_index,
    date: row.date,
    adjusted_weight: Math.round((adjusted[i] ?? rawCurve[i]) * 100) / 100,
  }));
};

/**
 * Detect likely incorrect / erroneous sample updates.
 *
 * Rule: If sample N causes a sharp drop from the prior growth trend AND
 *       sample N+1 recovers strongly toward the pre-drop trajectory,
 *       flag sample N as a probable erroneous sample.
 *
 * Disqualifiers (supporting evidence that the drop is real):
 *  - Two or more consecutive samples below the expected trend (pattern, not anomaly)
 *
 * @param {Array} deviations – per-sample deviation objects from calculateMissingFishIntelligence
 * @returns {{ detected: boolean, count: number, samples: Array, summary: string|null }}
 */
export const detectIncorrectSamples = (deviations) => {
  if (!deviations || deviations.length < 3) {
    return { detected: false, count: 0, samples: [], summary: null };
  }

  const flagged = [];

  for (let i = 1; i < deviations.length - 1; i++) {
    const prev = deviations[i - 1];
    const curr = deviations[i];
    const next = deviations[i + 1];

    const prevWeight = prev.actual_weight_g;
    const currWeight = curr.actual_weight_g;
    const nextWeight = next.actual_weight_g;
    const prevDays  = prev.days_since_arrival;
    const currDays  = curr.days_since_arrival;
    const nextDays  = next.days_since_arrival;

    // Prior daily growth rate (g/day)
    let priorGrowthRate;
    if (i >= 2) {
      const pp = deviations[i - 2];
      const dDays = prevDays - pp.days_since_arrival;
      priorGrowthRate = dDays > 0
        ? (prevWeight - pp.actual_weight_g) / dDays
        : (curr.model_weight_g - prev.model_weight_g) / Math.max(1, currDays - prevDays);
    } else {
      priorGrowthRate = (curr.model_weight_g - prev.model_weight_g) / Math.max(1, currDays - prevDays);
    }

    // Expected weight at current sample following the prior trend
    const daysDiffCurr = currDays - prevDays;
    const expectedAtCurr = prevWeight + priorGrowthRate * daysDiffCurr;
    if (expectedAtCurr <= 0) continue;

    const dropPercent = ((currWeight - expectedAtCurr) / expectedAtCurr) * 100;

    // Threshold: at least 8% below expected trend
    if (dropPercent >= -8) continue;

    // Recovery check
    const expectedAtNextPreDrop = prevWeight + priorGrowthRate * (nextDays - prevDays);
    const expectedAtNextPostDrop = currWeight + priorGrowthRate * (nextDays - currDays);

    const distPreDrop  = Math.abs(nextWeight - expectedAtNextPreDrop);
    const distPostDrop = Math.abs(nextWeight - expectedAtNextPostDrop);

    const recoveryConfirmed  = distPreDrop < distPostDrop;
    const recoveryPercent    = currWeight > 0 ? ((nextWeight - currWeight) / currWeight) * 100 : 0;
    const significantRecovery = recoveryPercent > 5;

    if (!(recoveryConfirmed && significantRecovery)) continue;

    // Disqualifier: consecutive low pattern
    if (i >= 2) {
      const pp = deviations[i - 2];
      const ppGrowthRate = i >= 3
        ? (deviations[i - 3].actual_weight_g !== undefined
            ? (pp.actual_weight_g - deviations[i - 3].actual_weight_g) /
              Math.max(1, pp.days_since_arrival - deviations[i - 3].days_since_arrival)
            : priorGrowthRate)
        : priorGrowthRate;
      const expectedPrev = i >= 3
        ? deviations[i - 3].actual_weight_g + ppGrowthRate * (prevDays - deviations[i - 3].days_since_arrival)
        : prev.model_weight_g;
      if (expectedPrev > 0 && ((prevWeight - expectedPrev) / expectedPrev) * 100 < -8) {
        continue; // Two consecutive drops → real pattern
      }
    }

    flagged.push({
      sampleIndex: i,
      sampleNumber: curr.sample_number,
      sampleDate: curr.sample_date,
      daysInWater: currDays,
      actualWeight: Math.round(currWeight * 10) / 10,
      expectedWeight: Math.round(expectedAtCurr * 10) / 10,
      dropPercent: Math.round(dropPercent * 10) / 10,
      recoveryPercent: Math.round(recoveryPercent * 10) / 10,
      nextSampleDate: next.sample_date,
      nextSampleWeight: Math.round(nextWeight * 10) / 10,
      classification: 'Flagged sample',
      severity: Math.abs(dropPercent) >= 15 ? 'High' : 'Moderate',
      explanation: null, // ★ PURGED — narrative from ssotOverride.js only
    });
  }

  return {
    detected: flagged.length > 0,
    count: flagged.length,
    samples: flagged,
    summary: null, // ★ PURGED — narrative from ssotOverride.js only
  };
};

/**
 * FCR-Based Missing Fish Detection
 *
 * Uses the SAME reported closing FCR produced by the shared calcClosingFCR
 * engine. Estimates missing fish algebraically:
 *   adjustedClosingBiomass = totalFeed / modelFCR − harvest − mortality + startBiomass
 *   removedBiomass = reportedClosingBiomass − adjustedClosingBiomass
 *   missingFish = removedBiomass / (lastWeightG / 1000)
 *
 * @param {Object}  params
 * @param {number}  params.reportedClosingFCR   - Pre-computed reported FCR from shared engine
 * @param {number}  params.modelFCR             - Model FCR from weight+strain lookup
 * @param {number}  params.totalFeedKg          - Cumulative feed (kg)
 * @param {number}  params.startBiomassKg       - Starting biomass (kg)
 * @param {number}  params.closingBiomassKg     - Reported closing biomass (kg)
 * @param {number}  params.harvestBiomassKg     - Total harvest biomass (kg)
 * @param {number}  params.mortalityBiomassKg   - Total mortality biomass (kg)
 * @param {number}  params.reportedRemaining    - Remaining fish at end (reported path)
 * @param {number}  params.lastWeightG          - Last average weight (g)
 * @param {Object}  [params.trajectoryJump]     - Jump detection result for cross-referencing
 * @returns {Object|null}
 */
export const detectFCRBasedMissingFish = ({
  reportedClosingFCR,
  modelFCR,
  totalFeedKg,
  startBiomassKg,
  closingBiomassKg,
  harvestBiomassKg = 0,
  mortalityBiomassKg = 0,
  reportedRemaining,
  lastWeightG,
  trajectoryJump = null,
}) => {
  const round2 = (v) => Math.round(v * 100) / 100;

  if (!reportedClosingFCR || reportedClosingFCR <= 0 ||
      !modelFCR || modelFCR <= 0 ||
      !totalFeedKg || totalFeedKg <= 0 ||
      !reportedRemaining || reportedRemaining <= 0 ||
      !lastWeightG || lastWeightG <= 0) {
    return null;
  }

  // Gate: reported FCR must be suspiciously better than model FCR
  // (allow 15% natural margin — FCR slightly better than model is normal)
  if (reportedClosingFCR >= modelFCR * 0.85) {
    return {
      detected: false,
      reportedBiologicalFCR: round2(reportedClosingFCR),
      modelBiologicalFCR: round2(modelFCR),
    };
  }

  // ── Algebraic solution ──────────────────────────────────────────────
  // FCR = Feed / BiomassGain  where BiomassGain = Closing + Harvest + Mortality − Start
  // For modelFCR:  adjustedGain = totalFeed / modelFCR
  // adjustedClosingBiomass = adjustedGain − harvest − mortality + startBiomass
  const adjustedGainKg = totalFeedKg / modelFCR;
  const adjustedClosingBiomassKg = adjustedGainKg - harvestBiomassKg - mortalityBiomassKg + startBiomassKg;

  if (adjustedClosingBiomassKg <= 0) return null;

  const removedBiomassKg = closingBiomassKg - adjustedClosingBiomassKg;
  if (removedBiomassKg <= 0) return null;

  // Convert removed biomass to fish count
  const weightPerFishKg = lastWeightG / 1000;
  const estimatedMissing = Math.round(removedBiomassKg / weightPerFishKg);

  if (estimatedMissing < reportedRemaining * 0.02) {
    return {
      detected: false,
      reportedBiologicalFCR: round2(reportedClosingFCR),
      modelBiologicalFCR: round2(modelFCR),
    };
  }

  const adjustedCount = Math.max(1, reportedRemaining - estimatedMissing);
  const missingPercent = (estimatedMissing / reportedRemaining) * 100;
  const adjustedFCR = adjustedGainKg > 0 ? totalFeedKg / adjustedGainKg : modelFCR;
  const fcrGapPercent = ((modelFCR - reportedClosingFCR) / modelFCR) * 100;

  // ── Confidence ────────────────────────────────────────────────────────
  let confidence;
  if (fcrGapPercent > 25 && missingPercent > 15) confidence = 'High';
  else if (fcrGapPercent > 15 || missingPercent > 10) confidence = 'Moderate';
  else confidence = 'Low';

  const jumpAlsoDetected = trajectoryJump?.jump_event_confirmed || trajectoryJump?.jump_event_detected;
  if (jumpAlsoDetected) {
    if (confidence === 'Low') confidence = 'Moderate';
    else if (confidence === 'Moderate') confidence = 'High';
  }

  const evidenceType = jumpAlsoDetected
    ? 'Corroborated by both FCR inconsistency and sample jump detection'
    : 'FCR/biomass inconsistency only — no sample jump corroboration';

  return {
    detected: true,
    reportedFishCount: reportedRemaining,
    adjustedFishCount: adjustedCount,
    estimatedMissingFish: estimatedMissing,
    missingPercent: round2(missingPercent),
    reportedBiologicalFCR: round2(reportedClosingFCR),
    modelBiologicalFCR: round2(modelFCR),
    adjustedBiologicalFCR: round2(adjustedFCR),
    fcrGapPercent: round2(fcrGapPercent),
    confidence,
    jumpCorroborated: !!jumpAlsoDetected,
    evidenceType,
    explanation:
      `Reported biological FCR (${round2(reportedClosingFCR)}) is ${round2(fcrGapPercent)}% better ` +
      `than the expected model FCR (${round2(modelFCR)}). ` +
      `Reducing the fish count from ${reportedRemaining.toLocaleString()} to ` +
      `${adjustedCount.toLocaleString()} (−${estimatedMissing.toLocaleString()} fish, ` +
      `${round2(missingPercent)}%) brings the recalculated FCR ` +
      `in line with expectations. ${evidenceType}.`,
  };
};

/**
 * Best-Fit Fish Number via Growth Curve Fitting
 *
 * Finds the fish count that produces the best-fit growth line through
 * all sample weight updates. Uses least-squares regression on the
 * relationship: with N fish instead of N_reported, each fish gets
 * (N_reported / N) times more feed, scaling the expected weight gain.
 *
 * adjusted_model_weight = starting_weight + (model_gain) × (N_reported / N)
 *
 * Optimal N is solved analytically via:
 *   ratio* = Σ(actual_gain × model_gain) / Σ(model_gain²)
 *   N* = N_reported / ratio*
 *
 * @param {Object} params
 * @param {number} params.reportedFishCount  - Reported/stocked fish count
 * @param {number} params.startingWeightG    - Stocking average weight (g)
 * @param {Array}  params.comparisons        - [{actual_weight_g, model_weight_g, sample_date, days_since_arrival}]
 * @returns {Object|null}
 */
export const findBestFitFishNumber = ({
  reportedFishCount,
  startingWeightG,
  comparisons,
}) => {
  if (!reportedFishCount || reportedFishCount <= 0 || !startingWeightG || !comparisons?.length) {
    return null;
  }

  const round2 = (v) => Math.round(v * 100) / 100;

  // Build gain arrays (actual and model gains from starting weight)
  const points = [];
  let sumAG = 0; // Σ(actual_gain × model_gain)
  let sumGG = 0; // Σ(model_gain²)

  for (const c of comparisons) {
    const aGain = c.actual_weight_g - startingWeightG;
    const mGain = c.model_weight_g - startingWeightG;
    if (mGain <= 0) continue; // Skip samples before any model growth
    sumAG += aGain * mGain;
    sumGG += mGain * mGain;
    points.push({ aGain, mGain, actual: c.actual_weight_g, model: c.model_weight_g, date: c.sample_date, day: c.days_since_arrival });
  }

  if (sumGG === 0 || points.length === 0) return null;

  // Optimal feed ratio: ratio = N_reported / N_bestfit
  const optimalRatio = sumAG / sumGG;
  if (optimalRatio <= 0) return null;

  const rawBestFit = Math.round(reportedFishCount / optimalRatio);
  // Clamp to reasonable range [30% .. 100%] of reported count
  const bestFitN = Math.max(Math.round(reportedFishCount * 0.30), Math.min(reportedFishCount, rawBestFit));

  if (bestFitN >= reportedFishCount * 0.98) {
    // Less than 2% difference — effectively no missing fish
    return {
      detected: false,
      bestFitFishNumber: reportedFishCount,
      reportedFishNumber: reportedFishCount,
      estimatedMissing: 0,
      missingPercent: 0,
      feedRatio: 1,
      rSquared: null,
      fitError: null,
    };
  }

  // Compute fit statistics at the best-fit N
  const bestRatio = reportedFishCount / bestFitN;
  let ssRes = 0; // Residual sum of squares
  let ssTot = 0; // Total sum of squares
  const meanActualGain = points.reduce((s, p) => s + p.aGain, 0) / points.length;
  const fittedPoints = [];

  for (const p of points) {
    const fittedWeight = startingWeightG + p.mGain * bestRatio;
    const residual = p.actual - fittedWeight;
    ssRes += residual * residual;
    ssTot += (p.aGain - meanActualGain) * (p.aGain - meanActualGain);
    fittedPoints.push({
      date: p.date,
      day: p.day,
      actualWeight: round2(p.actual),
      modelWeight: round2(p.model),
      fittedWeight: round2(fittedWeight),
      residual: round2(residual),
    });
  }

  const rSquared = ssTot > 0 ? round2(1 - ssRes / ssTot) : 0;
  const rmse = round2(Math.sqrt(ssRes / points.length));
  const estimatedMissing = reportedFishCount - bestFitN;
  const missingPercent = round2((estimatedMissing / reportedFishCount) * 100);

  return {
    detected: true,
    bestFitFishNumber: bestFitN,
    reportedFishNumber: reportedFishCount,
    estimatedMissing,
    missingPercent,
    feedRatio: round2(bestRatio),
    rSquared,
    fitError: rmse,
    fittedPoints,
    samplesUsed: points.length,
    explanation: null, // ★ PURGED — narrative from ssotOverride.js only
  };
};

export default {
  getModelFCR,
  getModelFCRSync,
  getExpectedFCR,
  calculateBiomassReconciliation,
  detectMissingFish,
  findEarliestActionableDate,
  calculateAvoidableFeed,
  classifyConfidence,
  runMissingFishDetection,
  calculateEconomicImpact,
  calculateDataQualityScore,
  checkMortalityReality,
  getSamplingRecommendation,
  detectOverfeeding,
  getGraphInsight,
  detectTrajectoryJump,
  detectConfirmedSampleJump,
  computeAdjustedActualCurve,
  detectIncorrectSamples,
  detectFCRBasedMissingFish,
  findBestFitFishNumber,
};
